import clsx from "clsx";
import { FunctionComponent } from "react";

import UserIconAlt from "@ui/components/icon/UserIconAlt";
import SubscribeNav from "@ui/components/navigation/Navigation/components/PrimaryNavigation/components/PrimaryNavigationStandard/components/SubscribeNav/SubscribeNav";
import { srOnly } from "@ui/shared-styles/accessibility.css";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { type NavItem } from "@voxmedia/duet-context/context/global-data-context";

import LinkMenu from "./components/LinkMenu";
import * as styles from "./PrimaryNavigationStandard.css";

export type PinnedNavItem = React.ReactElement | NavItem;

export interface RightMenuButtonsDesktopProps {
  variant: "standard" | "homepage";
  monochrome?: boolean;
}

type Props = {
  Logo: FunctionComponent<{ className?: string }>;
  items: PinnedNavItem[];
  rightMenuButtons: React.FunctionComponent<RightMenuButtonsDesktopProps>;
  cta: React.ReactElement | null;
  variant: "standard" | "homepage";
  showLinkLines: boolean;
  showSubscribeInNav?: boolean;
  linkClassName?: string;
  monochrome?: boolean;
};

const PrimaryNavigationStandard: FunctionComponent<Props> = ({
  items,
  Logo,
  showLinkLines,
  linkClassName,
  variant,
  rightMenuButtons: RightMenuButtons,
  monochrome,
}) => {
  const analytics = useAnalytics();

  const linkMenu = (
    <LinkMenu variant={variant}>
      {items.map((item, index) => {
        if ("title" in item) {
          return (
            <LinkMenu.Link
              key={index}
              className={linkClassName}
              href={item.url}
              showLinkLines={showLinkLines}
              special={item.special}
              icon={item.icon}
              variant={variant}
              monochrome={monochrome}
              onClick={() =>
                analytics?.emit({
                  event: "navigation_click",
                  navigation_click: { name: item.title },
                })
              }
            >
              {item.title}
            </LinkMenu.Link>
          );
        }
        return item;
      })}
    </LinkMenu>
  );

  const handleLogoClick = () =>
    analytics?.emit({
      event: "navigation_click",
      navigation_click: { name: "front page" },
    });

  const getSubscribeUrl = () => {
    const returnPath = encodeURIComponent(window.location.pathname);
    return `/support-membership?itm_campaign=top-nav-bam-button&itm_medium=site&itm_source=navigation&returnPath=${returnPath}`;
  };

  return (
    <div className={styles.container[monochrome ? "monochrome" : variant]}>
      <div className={styles.innerContainer}>
        <div className={styles.logoContainer[variant]}>
          <a href="/" onClick={handleLogoClick}>
            <span className={srOnly}>The homepage</span>
            <Logo
              className={clsx(
                monochrome && styles.monochrome,
                styles.logo[variant],
              )}
            />
          </a>
        </div>

        <nav
          className={styles.linkMenuContainer[variant]}
          aria-label="Top Navigation"
        >
          {linkMenu}
        </nav>
        <div className={styles.accountContainer[variant]}>
          <SubscribeNav
            subscribeText="Become a Member"
            signInText="Sign In"
            showSubscribeLinkFirst={false}
            leftIcon={
              <UserIconAlt width={8} height={14} className={styles.userIcon} />
            }
            getSubscribeUrl={getSubscribeUrl}
          />
        </div>
        <div className={styles.buttonsContainer[variant]}>
          <RightMenuButtons variant={variant} monochrome={monochrome} />
        </div>
      </div>
    </div>
  );
};

export default PrimaryNavigationStandard;
